import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import GoTrue from "gotrue-js"

const defaultState = {
  isLoggedIn: false,
  isValidating: false,
  setUser: () => {},
  getUser: () => {},
  getToken: async () => null,
  handleLogin: () => {},
  handleSignup: () => {},
  requestPasswordRecovery: () => {},
  handleConfirmation: () => {},
  handleInvitation: () => {},
  handleReset: () => {},
  handlePasswordReset: () => {},
  logout: () => {},
}

const apiEndpoint =
  typeof window !== "undefined"
    ? `${window.location.origin}/.netlify/identity`
    : "https://www.gba-group.com/.netlify/identity"

const auth = new GoTrue({
  APIUrl: apiEndpoint,
})

const ShopContext = React.createContext(defaultState)

const ShopProvider = ({ children }) => {
  const setUser = (user) =>
    (window.localStorage.shopUser = JSON.stringify(user))
  const getUser = () =>
    window.localStorage.shopUser ? JSON.parse(window.localStorage.shopUser) : {}

  const hasStoredUser = !!getUser().email
  const [isLoggedIn, setIsLoggedIn] = useState(hasStoredUser)
  const [isValidating, setIsValidating] = useState(hasStoredUser)

  const handleLogin = (email, password, callback) => {
    auth
      .login(email, password, true)
      .then(() => {
        setUser(auth.currentUser())
        setIsLoggedIn(true)
        callback(true, "Your are logged in")
      })
      .catch((error) => {
        setIsLoggedIn(false)
        callback(false, error?.json?.error_description || error?.message || "Login failed")
      })
  }

  const handleSignup = (email, password, callback) => {
    auth
      .signup(email, password)
      .then((response) => {
        setUser(response)
        callback(true, "Signup successful")
      })
      .catch((error) => {
        return callback(false, error?.json?.msg || error?.message || "Signup failed")
      })
  }

  const requestPasswordRecovery = (email, callback) => {
    auth
      .requestPasswordRecovery(email)
      .then((response) => {
        callback(true, "Recovery email sent")
      })
      .catch((error) => {
        return callback(false, error?.json?.msg || error?.message || "Recovery failed")
      })
  }

  const handleConfirmation = (token, callback) => {
    auth
      .confirm(token, true)
      .then(function (response) {
        setUser(response)
        callback(true, "Thanks for confirming, Please reset you Password")
      })
      .catch(function (error) {
        return callback(false, error?.json?.msg || error?.message || "Confirmation failed")
      })
  }

  const handleInvitation = (token, password, callback) => {
    auth
      .acceptInvite(token, password, true)
      .then(function (response) {
        setUser(response)
        setIsLoggedIn(true)
        callback(true, "Thanks for confirming, Password is set")
      })
      .catch(function (error) {
        return callback(false, error?.json?.msg || error?.message || "Invitation failed")
      })
  }

  const handleReset = (token, callback) => {
    auth
      .recover(token)
      .then((response) => {
        setUser(response)
        setIsLoggedIn(true)
        callback(true, "Reset your password")
      })
      .catch(function (error) {
        return callback(false, error?.json?.msg || error?.message || "Reset failed")
      })
  }

  const handlePasswordReset = (password, callback) => {
    const user = auth.currentUser()
    const email = user.email

    user
      .update({ email, password })
      .then(() => {
        callback("Password Updated")
      })
      .catch(function (error) {
        return callback(error?.json?.msg || error?.message || "Password reset failed")
      })
  }

  const getToken = async () => {
    const currentUser = auth.currentUser()
    if (!currentUser) return null
    try {
      const token = await currentUser.jwt()
      setUser(currentUser)
      return token
    } catch {
      setUser({})
      setIsLoggedIn(false)
      return null
    }
  }

  const logout = () => {
    const user = auth.currentUser()

    if (user) {
      user.logout().then(() => {
        setIsLoggedIn(false)
        setUser({})
      })
    } else {
      setIsLoggedIn(false)
      setUser({})
    }
  }

  useEffect(() => {
    const user = getUser()

    if (isLoggedIn) {
      const currentUser = auth.currentUser()
      if (!currentUser) {
        setUser({})
        setIsLoggedIn(false)
        setIsValidating(false)

        return
      }

      currentUser
        .jwt()
        .then(() => {
          setIsLoggedIn(!!user.email)
          setIsValidating(false)
        })
        .catch(() => {
          setIsLoggedIn(false)
          setIsValidating(false)
          setUser({})
        })
    }
  }, [isLoggedIn])

  return (
    <ShopContext.Provider
      value={{
        isLoggedIn,
        isValidating,
        setUser,
        getUser,
        getToken,
        handleLogin,
        handleSignup,
        requestPasswordRecovery,
        handleConfirmation,
        handleInvitation,
        handleReset,
        handlePasswordReset,
        logout,
      }}
    >
      {children}
    </ShopContext.Provider>
  )
}

ShopProvider.propTypes = {
  children: PropTypes.node,
}

export default ShopContext

export { ShopProvider }
